import { useEffect, useState } from "react";
import { http } from "../api";
import { Radio, MessageSquare, Send, CheckCircle2, Inbox, Users, X, Beaker } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState, PersonLink } from "../shared";

const empty = { title: "", body: "", channel: "SMS", audience: "All Ward 7", scheduled_at: "", status: "Draft", audience_ward: "", audience_booth: "", audience_inclination: "" };
const channels = ["SMS", "WhatsApp", "Email", "Push"];
const INCLINATIONS = ["Supporter", "Neutral", "Opposition", "Undecided", "BJP Worker", "TMC Worker", "CPIM Worker"];
function tone(s) { return { Sent: "green", Scheduled: "blue", Draft: "amber", Failed: "red" }[s] || "blue"; }

export default function Broadcasts() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [inbound, setInbound] = useState([]);
  const [testFrom, setTestFrom] = useState("");
  const [testText, setTestText] = useState("");
  const [testChannel, setTestChannel] = useState("WhatsApp");
  const [testBusy, setTestBusy] = useState(false);
  const [testReply, setTestReply] = useState(null);
  // Phase 30 — Recipients preview
  const [recPreview, setRecPreview] = useState(null); // { broadcast, loading, data }
  // Phase 30.1 — Test-send modal state
  const [testSend, setTestSend] = useState(null); // { broadcast, to, sending, result }
  // Phase 30 — live audience preview inside the compose modal
  const [audiencePreview, setAudiencePreview] = useState(null); // { count, sample, capped }

  useEffect(() => {
    if (!show) { setAudiencePreview(null); return undefined; }
    const t = setTimeout(async () => {
      const params = new URLSearchParams();
      if (form.audience_ward !== "" && form.audience_ward !== null) params.set("ward", form.audience_ward);
      if (form.audience_booth) params.set("booth", form.audience_booth);
      if (form.audience_inclination) params.set("inclination", form.audience_inclination);
      try {
        const r = await http.get(`/broadcasts/audience/preview?${params.toString()}`);
        setAudiencePreview(r.data);
      } catch { setAudiencePreview(null); }
    }, 260);
    return () => clearTimeout(t);
  }, [show, form.audience_ward, form.audience_booth, form.audience_inclination]);

  const openRecipients = async (bc) => {
    setRecPreview({ broadcast: bc, loading: true, data: null });
    try {
      const r = await http.get(`/broadcasts/${bc.broadcast_id}/recipients?limit=500`);
      setRecPreview({ broadcast: bc, loading: false, data: r.data });
    } catch (e) {
      setRecPreview({ broadcast: bc, loading: false, data: { error: e.response?.data?.detail || e.message } });
    }
  };

  const openTestSend = (bc) => setTestSend({ broadcast: bc, to: "", sending: false, result: null });
  const fireTestSend = async () => {
    if (!testSend?.to?.trim()) return;
    setTestSend((s) => ({ ...s, sending: true, result: null }));
    try {
      const r = await http.post(`/broadcasts/${testSend.broadcast.broadcast_id}/test-send`, { to: testSend.to.trim() });
      setTestSend((s) => ({ ...s, sending: false, result: r.data }));
    } catch (e) {
      setTestSend((s) => ({ ...s, sending: false, result: { error: e.response?.data?.detail || e.message } }));
    }
  };

  const load = () => http.get("/broadcasts").then((r) => setRows(r.data));
  const loadInbound = () => http.get("/inbound/log").then((r) => setInbound(r.data)).catch(() => {});
  useEffect(() => { load(); loadInbound(); http.get("/messages/status").then((r) => setStatus(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        audience_ward: form.audience_ward === "" || form.audience_ward === null ? null : Number(form.audience_ward),
        audience_booth: form.audience_booth || null,
        audience_inclination: form.audience_inclination || null,
      };
      await http.post("/broadcasts", payload);
      setShow(false); setForm(empty); load();
    }
    finally { setSaving(false); }
  };

  const send = async (bc) => {
    if (!window.confirm(`Send "${bc.title}" via ${bc.channel} to up to 200 recipients?`)) return;
    setSending(bc.broadcast_id);
    try {
      const r = await http.post(`/broadcasts/${bc.broadcast_id}/send`);
      setLastResult(r.data);
      load();
    } catch (err) {
      setLastResult({ error: err.response?.data?.detail || err.message });
    } finally { setSending(""); }
  };

  const simulateInbound = async () => {
    if (!testFrom || !testText) return;
    setTestBusy(true); setTestReply(null);
    try {
      const r = await http.post("/inbound/whatsapp", { from_number: testFrom, text: testText, channel: testChannel });
      setTestReply(r.data);
      loadInbound();
    } catch (err) {
      setTestReply({ error: err.response?.data?.detail || err.message });
    } finally { setTestBusy(false); }
  };

  return (
    <>
      <PageHeader eyebrow="OUTREACH HUB" title="Broadcasts" subtitle="Compose and send multi-channel messages via Twilio SMS / WhatsApp"
        actions={<AddButton onClick={() => setShow(true)} label="New broadcast" testid="broadcasts-add-button" />} />

      <div className="notice-strip" data-testid="messaging-status">
        <MessageSquare size={16} />
        {status?.configured
          ? <span><b>Twilio live</b> · SMS {status.has_sms_from ? "ready" : "sender missing"} · WhatsApp {status.has_whatsapp_from ? "ready" : "sender missing"}. Messages will be delivered for real.</span>
          : <span>Broadcasts run in <b>simulation mode</b>. Add <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_FROM_NUMBER</code> and <code>TWILIO_WHATSAPP_FROM</code> in <code>backend/.env</code> to send real SMS/WhatsApp.</span>}
      </div>

      {lastResult && (
        <div className="notice-strip" style={{ background: "#e5f2e3", color: "#3a7647", borderColor: "#c1dbb9" }} data-testid="broadcast-send-result">
          <CheckCircle2 size={16} />
          {lastResult.error
            ? <span>Send failed: {lastResult.error}</span>
            : <span>Send done via <b>{lastResult.provider}</b> · Sent {lastResult.sent} · Simulated {lastResult.simulated} · Failed {lastResult.failed}</span>}
        </div>
      )}

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={Radio} title="No broadcasts" hint="Compose a message to reach your audience." /> : (
          <div className="broadcast-list">
            {rows.map((b, i) => (
              <div key={b.broadcast_id} className="broadcast-row" data-testid={`broadcast-row-${i}`}>
                <div style={{ flex: 1 }}>
                  <div className="b-head"><span className="chip blue">{b.channel}</span><b>{b.title}</b><span className={`chip ${tone(b.status)}`}>{b.status}</span></div>
                  <p className="hint">{b.body}</p>
                  <small>To: <b>{b.audience}</b> · Scheduled: {b.scheduled_at || "Immediate"} · {b.delivered || 0}/{b.recipients || 0} delivered{b.provider_status ? ` · ${b.provider_status}` : ""}</small>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button className="outline" data-testid={`broadcast-recipients-${i}`} onClick={() => openRecipients(b)}>
                    <Users size={14} /> Recipients
                  </button>
                  <button className="outline" data-testid={`broadcast-test-${i}`} onClick={() => openTestSend(b)}
                    title="Send a single test message to your own phone before blasting the audience">
                    <Beaker size={14} /> Test send
                  </button>
                  <button className="primary" data-testid={`broadcast-send-${i}`} disabled={sending === b.broadcast_id || b.status === "Sent"} onClick={() => send(b)}>
                    <Send size={14} /> {sending === b.broadcast_id ? "Sending…" : b.status === "Sent" ? "Sent" : "Send"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel" data-testid="autoreply-panel" style={{ marginTop: 18 }}>
        <div className="panel-head">
          <div>
            <h2>Language Auto-Reply</h2>
            <p>Simulate an inbound WhatsApp/SMS. We match the sender to a contact and reply in their preferred language.</p>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, padding: "0 4px" }}>
          <label className="frow" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".3px", color: "var(--muted)" }}>From (mobile)</span>
            <input data-testid="inbound-from-input" placeholder="+919876543210" value={testFrom} onChange={(e) => setTestFrom(e.target.value)} />
          </label>
          <label className="frow" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".3px", color: "var(--muted)" }}>Channel</span>
            <select data-testid="inbound-channel-input" value={testChannel} onChange={(e) => setTestChannel(e.target.value)}>
              <option>WhatsApp</option><option>SMS</option>
            </select>
          </label>
        </div>
        <label className="frow" style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 4px 0" }}>
          <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".3px", color: "var(--muted)" }}>Message</span>
          <textarea rows={2} data-testid="inbound-text-input" placeholder="status of my complaint?" value={testText} onChange={(e) => setTestText(e.target.value)} />
        </label>
        <div className="form-actions" style={{ marginTop: 10 }}>
          <button className="primary" data-testid="inbound-send-button" disabled={testBusy || !testFrom || !testText} onClick={simulateInbound}>
            <Inbox size={14} /> {testBusy ? "Routing…" : "Simulate inbound"}
          </button>
        </div>
        {testReply && (
          <div className="notice-strip" data-testid="inbound-reply-result" style={{ background: testReply.error ? "#fdecea" : "#e5f2e3", color: testReply.error ? "#b04a3f" : "#3a7647", borderColor: "#c1dbb9", marginTop: 10 }}>
            {testReply.error
              ? <span>Error: {testReply.error}</span>
              : <span>Matched: <b>{testReply.matched_contact || "unknown"}</b> · Lang: <b>{testReply.language?.toUpperCase()}</b> · Reply: “{testReply.reply}” · Delivery: <b>{testReply.delivery?.status}</b></span>}
          </div>
        )}

        <h4 style={{ marginTop: 16, fontSize: 12 }}>RECENT INBOUND ({inbound.length})</h4>
        {inbound.length === 0 ? <p className="hint">No inbound messages yet.</p> : (
          <div className="broadcast-list" data-testid="inbound-log" style={{ paddingBottom: 60 }}>
            {inbound.slice(0, 15).map((m, i) => (
              <div key={m.inbound_id} className="broadcast-row" data-testid={`inbound-row-${i}`}>
                <div style={{ flex: 1 }}>
                  <div className="b-head">
                    <span className="chip blue">{m.channel}</span>
                    <b>{m.from_number}</b>
                    <span className="chip green">{(m.language || "en").toUpperCase()}</span>
                  </div>
                  <p className="hint">In: {m.text}</p>
                  <p className="hint" style={{ color: "var(--ink)" }}>Reply: {m.reply}</p>
                  <small>{new Date(m.created_at).toLocaleString()}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title="New broadcast" onClose={() => setShow(false)} testid="broadcast-modal">
          <FormRow label="Title"><input data-testid="form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
          <FormRow label="Message body"><textarea data-testid="form-body-input" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Namaskar #FirstName, …" /></FormRow>
          <div className="form-grid">
            <FormRow label="Channel"><select data-testid="form-channel-input" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>{channels.map((c) => <option key={c}>{c}</option>)}</select></FormRow>
            <FormRow label="Audience label"><input data-testid="form-audience-input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} /></FormRow>
            <FormRow label="Schedule at"><input data-testid="form-schedule-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></FormRow>
            <FormRow label="Status"><select data-testid="form-status-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option>Draft</option><option>Scheduled</option><option>Sent</option></select></FormRow>
          </div>

          <section className="panel" data-testid="audience-filters" style={{ marginTop: 12, padding: "12px 14px", background: "#ecfdf5", borderRadius: 10 }}>
            <h4 style={{ margin: "0 0 8px", fontSize: 12, letterSpacing: ".4px", color: "#065f46" }}>AUDIENCE FILTERS</h4>
            <div className="form-grid">
              <FormRow label="Ward">
                <input data-testid="form-audience-ward-input" type="number" placeholder="Any" value={form.audience_ward}
                       onChange={(e) => setForm({ ...form, audience_ward: e.target.value })} />
              </FormRow>
              <FormRow label="Booth">
                <input data-testid="form-audience-booth-input" placeholder="Any" value={form.audience_booth}
                       onChange={(e) => setForm({ ...form, audience_booth: e.target.value })} />
              </FormRow>
              <FormRow label="Inclination">
                <select data-testid="form-audience-inclination-input" value={form.audience_inclination}
                        onChange={(e) => setForm({ ...form, audience_inclination: e.target.value })}>
                  <option value="">Any</option>
                  {INCLINATIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </FormRow>
            </div>
            <div data-testid="audience-preview-strip" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: "#065f46" }}>
              <b>{audiencePreview ? `${audiencePreview.count} recipients` : "…"}</b>
              {audiencePreview?.capped && <span className="chip amber">Capped at 2000</span>}
              {audiencePreview?.sample?.length > 0 && (
                <span style={{ color: "#0f766e" }}>
                  e.g. {audiencePreview.sample.map((s) => s.name).slice(0, 3).join(", ")}
                  {audiencePreview.sample.length > 3 ? "…" : ""}
                </span>
              )}
              {audiencePreview?.count === 0 && (
                <span style={{ color: "#92400e" }}>No matches — loosen the filters.</span>
              )}
            </div>
          </section>

          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.title} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}

      {testSend && (
        <Modal title={`Test send · ${testSend.broadcast.title}`} onClose={() => setTestSend(null)} testid="test-send-modal">
          <p className="hint" style={{ margin: "0 0 12px" }}>
            Fire a single message to a mobile you control. This does not affect delivery counters or the audience list — great for eyeballing copy on real hardware before you blast the ward.
          </p>
          <div className="hh-meta" style={{ marginBottom: 12 }}>
            <span className="chip blue">{testSend.broadcast.channel}</span>
            <span>Audience: <b>{testSend.broadcast.audience}</b></span>
          </div>
          <FormRow label="Send to mobile">
            <input
              data-testid="test-send-to-input"
              value={testSend.to}
              placeholder="+919876543210"
              onChange={(e) => setTestSend((s) => ({ ...s, to: e.target.value }))}
              autoFocus
            />
          </FormRow>
          <p className="hint" style={{ marginTop: 6, fontSize: 11 }}>
            #FirstName and #Name will be personalised with your own name.
          </p>
          <div className="form-actions">
            <button className="outline" data-testid="test-send-cancel" onClick={() => setTestSend(null)}>Cancel</button>
            <button className="primary" data-testid="test-send-fire"
                    disabled={testSend.sending || !testSend.to.trim()}
                    onClick={fireTestSend}>
              <Beaker size={14} /> {testSend.sending ? "Sending…" : "Fire test"}
            </button>
          </div>
          {testSend.result && (
            <div
              data-testid="test-send-result"
              className="notice-strip"
              style={{
                marginTop: 12,
                background: testSend.result.error ? "#fdecea" : "#e5f2e3",
                color: testSend.result.error ? "#b04a3f" : "#3a7647",
                borderColor: "#c1dbb9",
              }}
            >
              {testSend.result.error ? (
                <span>Error: {testSend.result.error}</span>
              ) : (
                <span>
                  <b>{testSend.result.status}</b> · Provider: {testSend.result.provider}
                  {testSend.result.provider_sid ? ` · SID ${testSend.result.provider_sid}` : ""}
                  <br />
                  <small>Preview: “{testSend.result.preview_body}”</small>
                </span>
              )}
            </div>
          )}
        </Modal>
      )}

      {recPreview && (
        <Modal title={`Recipients · ${recPreview.broadcast.title}`} onClose={() => setRecPreview(null)} testid="recipients-modal">
          {recPreview.loading ? (
            <p className="hint" style={{ padding: 20, textAlign: "center" }}>Loading recipients…</p>
          ) : recPreview.data?.error ? (
            <p className="hint" style={{ color: "#b04a3f" }}>Error: {recPreview.data.error}</p>
          ) : (
            <>
              <div className="hh-meta" data-testid="recipients-meta" style={{ marginBottom: 10 }}>
                <span className="chip blue">{recPreview.data.channel}</span>
                <span>Total {recPreview.data.total}</span>
                <span>Showing {recPreview.data.shown}</span>
                <span>Audience: <b>{recPreview.data.audience}</b></span>
              </div>
              <div data-testid="recipients-list" style={{ maxHeight: 420, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 10 }}>
                {(recPreview.data.recipients || []).map((r, i) => (
                  <div key={r.contact_id || i} className="broadcast-row" data-testid={`recipient-row-${i}`} style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)" }}>
                    <div style={{ flex: 1 }}>
                      <PersonLink contactId={r.contact_id} name={r.name} testid={`recipient-link-${i}`}>
                        <b>{r.name}</b>
                      </PersonLink>
                      <small style={{ display: "block", color: "var(--muted)", marginTop: 3 }}>
                        {r.mobile}{r.booth ? ` · Booth ${r.booth}` : ""}{r.inclination ? ` · ${r.inclination}` : ""}
                      </small>
                    </div>
                    {r.delivery_status && (
                      <span className={`chip ${r.delivery_status === "delivered" || r.delivery_status === "sent" ? "green" : r.delivery_status === "failed" ? "red" : "amber"}`}>
                        {r.delivery_status}
                      </span>
                    )}
                  </div>
                ))}
                {(!recPreview.data.recipients || recPreview.data.recipients.length === 0) && (
                  <p className="hint" style={{ padding: 24, textAlign: "center" }}>No recipients matched.</p>
                )}
              </div>
              <small className="hint" style={{ display: "block", marginTop: 8 }}>
                Names are clickable — opens the person profile in the CRM.
              </small>
            </>
          )}
        </Modal>
      )}
    </>
  );
}
